// CatalogPage - Listino Materiali (filtri per tipologia, CRUD articoli, import/export Excel)
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { Package, Plus, Loader2 } from 'lucide-react';
import ExcelCatalogManager from '../components/ExcelCatalogManager';
import { MAIN_TYPE_CODES } from '../components/catalog/catalogShared';
import { CatalogTypeSidebar } from '../components/catalog/CatalogTypeSidebar';
import { CatalogItemsTable } from '../components/catalog/CatalogItemsTable';
import { CatalogItemFormDialog } from '../components/catalog/CatalogItemFormDialog';
import { CatalogItemDetailDialog } from '../components/catalog/CatalogItemDetailDialog';
import { CatalogActionDialog } from '../components/catalog/CatalogActionDialog';

export default function CatalogPage() {
  const [types, setTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);

  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemFormData, setItemFormData] = useState({});
  const [saving, setSaving] = useState(false);

  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionItem, setActionItem] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadTypes = useCallback(async () => {
    try {
      const response = await api.get('/catalog/types');
      setTypes(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle tipologie');
    }
  }, []);

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedType) params.append('type_code', selectedType);
      if (searchQuery) params.append('search', searchQuery);
      params.append('active_only', 'false');

      const response = await api.get(`/catalog/items?${params.toString()}`);
      setItems(response.data.items || []);
    } catch (error) {
      toast.error('Errore nel caricamento degli articoli');
    } finally {
      setItemsLoading(false);
    }
  }, [selectedType, searchQuery]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadTypes();
      setLoading(false);
    };
    init();
  }, [loadTypes]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const getTypeInfo = useCallback((typeCode) => {
    return types.find(t => t.code === typeCode);
  }, [types]);

  const handleCreate = () => {
    setEditingItem(null);
    setItemFormData({
      item_code: '',
      type_code: selectedType || '',
      brand: '',
      model: '',
      description: '',
      technical_specs: {},
      prezzo_listino: '',
      is_active: true,
      notes: '',
    });
    setShowItemDialog(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setItemFormData({
      item_code: item.item_code || '',
      type_code: item.type_code || '',
      brand: item.brand || '',
      model: item.model || '',
      description: item.description || '',
      technical_specs: item.technical_specs || {},
      prezzo_listino: item.prezzo_listino || '',
      is_active: item.is_active !== false,
      notes: item.notes || '',
    });
    setShowItemDialog(true);
  };

  const handleViewDetail = async (item) => {
    try {
      const response = await api.get(`/catalog/items/${item.id}`);
      setDetailItem(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Errore nel caricamento dei dettagli');
    }
  };

  const handleSave = async () => {
    if (!itemFormData.item_code || !itemFormData.type_code || !itemFormData.brand || !itemFormData.model) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...itemFormData,
        prezzo_listino: itemFormData.prezzo_listino ? parseFloat(itemFormData.prezzo_listino) : null,
      };

      if (editingItem) {
        await api.put(`/catalog/items/${editingItem.id}`, payload);
        toast.success('Articolo aggiornato');
      } else {
        await api.post('/catalog/items', payload);
        toast.success('Articolo creato');
      }

      setShowItemDialog(false);
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const openActionDialog = (item, type) => {
    setActionItem(item);
    setActionType(type);
    setShowActionDialog(true);
  };

  const executeAction = async () => {
    if (!actionItem || !actionType) return;

    setActionLoading(true);
    try {
      if (actionType === 'delete') {
        await api.delete(`/catalog/items/${actionItem.id}?hard_delete=true`);
        toast.success('Articolo eliminato definitivamente');
      } else if (actionType === 'deactivate') {
        await api.delete(`/catalog/items/${actionItem.id}`);
        toast.success('Articolo disattivato');
      } else if (actionType === 'activate') {
        await api.patch(`/catalog/items/${actionItem.id}/activate`);
        toast.success('Articolo riattivato');
      }

      loadItems();
      setShowActionDialog(false);
      setActionItem(null);
      setActionType(null);
    } catch (error) {
      console.error('Errore azione:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'operazione');
    } finally {
      setActionLoading(false);
    }
  };

  const selectedTypeSchema = useMemo(() => {
    if (!itemFormData.type_code) return [];
    const typeInfo = getTypeInfo(itemFormData.type_code);
    return typeInfo?.field_schema || [];
  }, [itemFormData.type_code, getTypeInfo]);

  const mainTypes = useMemo(() => {
    return types.filter(t => MAIN_TYPE_CODES.includes(t.code));
  }, [types]);

  const otherTypes = useMemo(() => {
    return types.filter(t => !MAIN_TYPE_CODES.includes(t.code));
  }, [types]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6 text-primary" />
            Listino Materiali
          </h1>
          <p className="text-muted-foreground">
            Catalogo componenti tecnici per impianti fotovoltaici
          </p>
        </div>
        <Button onClick={handleCreate} data-testid="create-item-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Articolo
        </Button>
      </div>

      <ExcelCatalogManager onImportComplete={loadItems} />

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-3">
          <CatalogTypeSidebar
            mainTypes={mainTypes}
            otherTypes={otherTypes}
            selectedType={selectedType}
            onSelectType={setSelectedType}
            totalCount={items.length}
          />
        </div>

        <div className="col-span-9">
          <CatalogItemsTable
            items={items}
            itemsLoading={itemsLoading}
            selectedType={selectedType}
            getTypeInfo={getTypeInfo}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onRefresh={loadItems}
            onCreate={handleCreate}
            onViewDetail={handleViewDetail}
            onEdit={handleEdit}
            onAction={openActionDialog}
          />
        </div>
      </div>

      <CatalogItemFormDialog
        open={showItemDialog}
        onOpenChange={setShowItemDialog}
        editingItem={editingItem}
        itemFormData={itemFormData}
        setItemFormData={setItemFormData}
        types={types}
        selectedTypeSchema={selectedTypeSchema}
        saving={saving}
        onSave={handleSave}
      />

      <CatalogItemDetailDialog
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        detailItem={detailItem}
        onEdit={(item) => {
          setShowDetailDialog(false);
          handleEdit(item);
        }}
      />

      <CatalogActionDialog
        open={showActionDialog}
        onOpenChange={setShowActionDialog}
        actionItem={actionItem}
        actionType={actionType}
        actionLoading={actionLoading}
        onConfirm={executeAction}
      />
    </div>
  );
}
